/* eslint-disable @typescript-eslint/naming-convention */
import parsePhoneNumberFromString, { isValidPhoneNumber } from 'libphonenumber-js';
import * as Yup from 'yup';

import {
  checkFutureDate,
  checkValidDate,
  isValidAddress,
  isValidDate,
  isValidEmail,
  isValidName,
  isValidZipCode,
} from '../../../../../Utils/validation';

export function atLeastOneFieldNotEmpty(fields: (string | undefined)[]): boolean {
  return fields.some((field) => !!field);
}

/** RHF / MUI selects may submit `''` or `null` before a pick; Yup `.array()` rejects those as type errors. */
export function coerceEmptyToOrganizationArray(value: unknown, originalValue: unknown): unknown[] {
  const raw = originalValue !== undefined ? originalValue : value;
  if (raw === '' || raw === null || raw === undefined) return [];
  if (Array.isArray(raw)) return raw;
  return [];
}

/**
 * Validates organization multi-select values as arrays, after coercing empty UI state to `[]`.
 * When `requireAtLeastOne` is false, an empty selection is allowed (collector / quick-add).
 */
export function organizationMultiSelectPickSchema(
  requireAtLeastOne: boolean,
  requiredMessage: string,
  shapeMessage = 'Choose one or more organizations using the organization field above.',
) {
  return Yup.mixed()
    .transform((value, originalValue) => coerceEmptyToOrganizationArray(value, originalValue))
    .test('organization-is-array', shapeMessage, (v) => Array.isArray(v))
    .test(
      'organization-required-count',
      requiredMessage,
      (v) => !requireAtLeastOne || (Array.isArray(v) && (v as unknown[]).length > 0),
    );
}

const carrierSelected = (id: unknown): boolean => {
  const n = Number(id);
  return Number.isInteger(n) && n > 0;
};

export const validationSchema = (
  addSecondaryInsurance: boolean,
  isQuickAdd: boolean,
  isCollectorAddUser = false,
  clientTagsRequired = false,
  isSendingInvitation = false,
  divisionRequired = false,
): any => {
  const requireCoreIdentity = isQuickAdd || isCollectorAddUser;
  // The invitation email is addressed by name, and the API rejects an invite
  // without a full name — so require first/last name whenever we send one.
  const requireName = requireCoreIdentity || isSendingInvitation;
  return Yup.object().shape({
    profile_img: Yup.string(),
    first_name: Yup.string().test('test-name', 'Enter Valid First Name', (value: any) => {
      if (requireName) {
        return value && isValidName(value);
      }
      if (value && !isValidName(value)) {
        return false;
      }
      return true;
    }),
    middle_name: Yup.string().test('test-name', 'Enter Valid Middle Name', (value: any) => {
      if (value && !isValidName(value)) {
        return false;
      }
      return true;
    }),
    last_name: Yup.string().test('test-name', 'Enter Valid Last Name', (value: any) => {
      if (requireName) {
        return value && isValidName(value);
      }
      if (value && !isValidName(value)) {
        return false;
      }
      return true;
    }),
    date_of_birth: Yup.string()
      .test('test-name', 'Enter Valid Date  MM/DD/YYYY', (value: any) => {
        if (requireCoreIdentity) {
          const values = value?.split('/') || [];
          return value && isValidDate(value) ? true : value && checkValidDate(values[0], values[1], values[2]);
        }
        if (value && !isValidDate(value)) {
          return false;
        }
        if (value) {
          const values = value?.split('/');
          if (!checkValidDate(values[0], values[1], values[2])) {
            return false;
          }
        }
        return true;
      })
      .test('test-future', 'The Date of Birth can not be in the future.', (value: any) => {
        if (value) {
          const values = value?.split('/');
          if (!checkFutureDate(values[0], values[1], values[2])) {
            return false;
          }
        }
        return true;
      }),
    language: Yup.array(),
    address1: Yup.string().test(
      'test-name',
      'Valid Address is required when at least one Address field is provided',
      function () {
        const { address1, address2, city, state, zip_code } = this.parent as Record<string, string | undefined>;
        if (atLeastOneFieldNotEmpty([address1, address2, city, state, zip_code])) {
          return !!address1 && isValidAddress(address1);
        }
        return true;
      },
    ),
    address2: Yup.string(),
    city: Yup.string().test(
      'test-required',
      'City is required when at least one Address field is provided',
      function () {
        const { address1, address2, city, state, zip_code } = this.parent as Record<string, string | undefined>;
        if (atLeastOneFieldNotEmpty([address1, address2, city, state, zip_code])) {
          return !!city;
        }
        return true;
      },
    ),
    state: Yup.string().test(
      'test-required',
      'State is required when at least one Address field is provided',
      function () {
        const { address1, address2, city, state, zip_code } = this.parent as Record<string, string | undefined>;
        if (atLeastOneFieldNotEmpty([address1, address2, city, state, zip_code])) {
          return !!state;
        }
        return true;
      },
    ),
    zip_code: Yup.string().test(
      'test-name',
      'Valid Zip Code is required when at least one Address field is provided',
      function () {
        const { address1, address2, city, state, zip_code } = this.parent as Record<string, string | undefined>;
        if (atLeastOneFieldNotEmpty([address1, address2, city, state, zip_code])) {
          return !!zip_code && isValidZipCode(zip_code);
        }
        return true;
      },
    ),
    phone_number: Yup.string().test('valid-us-phone', 'Enter Valid Phone Number', (value) => {
      // Optional → empty is allowed
      if (!value) return true;

      const phoneNumber = parsePhoneNumberFromString(value, 'US');

      return phoneNumber?.isValid() ?? false;
    }),
    email: isQuickAdd
      ? Yup.string().test('test-name', 'Enter Valid Email', (value: any) => {
          if (value && !isValidEmail(value)) {
            return false;
          }
          return true;
        })
      : Yup.string()
          .required('Email is required')
          .test('test-name', 'Enter Valid Email', (value: any) => {
            if (value && !isValidEmail(value)) {
              return false;
            }
            return true;
          }),
    // Patients always belong to Dentistry.One, so there is no organization (or organization
    // division/practice) for the user to pick and nothing to require here.
    organization: Yup.mixed(),
    // Primary insurance — fields ordered to match the visual layout in InsuranceFormSection.
    // All conditions use .test() (not .when()) so Yup's topological sort does not reorder these
    // fields — validation runs in schema declaration order = visual order.
    insurance_carrier_id: Yup.number().test('ins-carrier-required', 'Insurance carrier is required', function (value) {
      const p = this.parent as Record<string, unknown>;
      if (!!p.insurance_through && String(p.insurance_through) !== 'none') {
        return value !== undefined && value !== null && Number(value) !== 0;
      }
      return true;
    }),
    member_id: Yup.string()
      .test('member-id-required', 'Member ID is required', function (value) {
        const p = this.parent as Record<string, unknown>;
        if (
          carrierSelected(p.insurance_carrier_id) ||
          (!!p.insurance_through && String(p.insurance_through) !== 'none')
        ) {
          return !!value;
        }
        return true;
      })
      .test('member-id-no-empty-space', 'Member ID cannot be empty spaces', (value) => {
        if (value) return !/^\s+$/.test(value);
        return true;
      }),
    group_number: Yup.string().nullable(),
    // InsuranceFormSection uses insurance_phone_number; InsuranceFields uses iPhoneNumber — accept either.
    insurance_phone_number: Yup.string()
      .test('pri-phone-required', 'Insurance phone number is required', function (value) {
        const p = this.parent as Record<string, unknown>;
        if (
          carrierSelected(p.insurance_carrier_id) ||
          (!!p.insurance_through && String(p.insurance_through) !== 'none')
        ) {
          const modern = String(value ?? '').trim();
          const legacy = String(p.iPhoneNumber ?? '').trim();
          return !!(modern || legacy);
        }
        return true;
      })
      .test('pri-phone-valid', 'Enter Valid Phone Number', function (value) {
        const p = this.parent as Record<string, unknown>;
        const chosen = String(value ?? '').trim() || String(p.iPhoneNumber ?? '').trim();
        if (!chosen) return true;
        return isValidPhoneNumber(chosen);
      }),
    iPhoneNumber: Yup.string(),
    insurance_address: Yup.string()
      .test('ins-address-required', 'Insurance Address is required', function (value) {
        const p = this.parent as Record<string, unknown>;
        if (
          carrierSelected(p.insurance_carrier_id) ||
          (!!p.insurance_through && String(p.insurance_through) !== 'none')
        ) {
          return !!value;
        }
        return true;
      })
      .test('ins-address-no-empty-space', 'Insurance Address cannot be empty spaces', (value) => {
        if (value) return !/^\s+$/.test(value);
        return true;
      }),
    insurance_city: Yup.string()
      .test('ins-city-required', 'Insurance City is required', function (value) {
        const p = this.parent as Record<string, unknown>;
        if (
          carrierSelected(p.insurance_carrier_id) ||
          (!!p.insurance_through && String(p.insurance_through) !== 'none')
        ) {
          return !!value;
        }
        return true;
      })
      .test('ins-city-no-empty-space', 'Insurance City cannot be empty spaces', (value) => {
        if (value) return !/^\s+$/.test(value);
        return true;
      }),
    insurance_state: Yup.string()
      .test('ins-state-required', 'Insurance State is required', function (value) {
        const p = this.parent as Record<string, unknown>;
        if (
          carrierSelected(p.insurance_carrier_id) ||
          (!!p.insurance_through && String(p.insurance_through) !== 'none')
        ) {
          return !!value;
        }
        return true;
      })
      .test('ins-state-no-empty-space', 'Insurance State cannot be empty spaces', (value) => {
        if (value) return !/^\s+$/.test(value);
        return true;
      }),
    insurance_zip_code: Yup.string()
      .test('ins-zip-required', 'Insurance Zip Code is required', function (value) {
        const p = this.parent as Record<string, unknown>;
        if (
          carrierSelected(p.insurance_carrier_id) ||
          (!!p.insurance_through && String(p.insurance_through) !== 'none')
        ) {
          return !!value;
        }
        return true;
      })
      .test('ins-zip-no-empty-space', 'Insurance Zip Code cannot be empty spaces', (value) => {
        if (value) return !/^\s+$/.test(value);
        return true;
      }),
    insurance_through: Yup.string().test(
      'ins-through-required',
      'Please select who the policy holder is',
      function (value) {
        const p = this.parent as Record<string, unknown>;
        if (carrierSelected(p.insurance_carrier_id)) {
          return !!value && value !== 'none';
        }
        return true;
      },
    ),
    insurance_first_name: Yup.string()
      .test('ins-fn-required', 'Insurance Holder First Name is required', function (value) {
        const p = this.parent as Record<string, unknown>;
        const through = String(p.insurance_through ?? '');
        if (carrierSelected(p.insurance_carrier_id) && through && through !== 'none' && through !== 'myself') {
          return !!value;
        }
        return true;
      })
      .test('ins-fn-valid', 'Enter Valid First Name', (value) => {
        if (value && !isValidName(value)) return false;
        return true;
      }),
    insurance_last_name: Yup.string()
      .test('ins-ln-required', 'Insurance Holder Last Name is required', function (value) {
        const p = this.parent as Record<string, unknown>;
        const through = String(p.insurance_through ?? '');
        if (carrierSelected(p.insurance_carrier_id) && through && through !== 'none' && through !== 'myself') {
          return !!value;
        }
        return true;
      })
      .test('ins-ln-valid', 'Enter Valid Last Name', (value) => {
        if (value && !isValidName(value)) return false;
        return true;
      }),
    insurance_date_of_birth: Yup.string()
      .test('ins-dob-required', 'Insurance Holder Date of birth is required', function (value) {
        const p = this.parent as Record<string, unknown>;
        const through = String(p.insurance_through ?? '');
        if (carrierSelected(p.insurance_carrier_id) && through && through !== 'none' && through !== 'myself') {
          return !!value;
        }
        return true;
      })
      .test('ins-dob-valid', 'Enter Valid Date MM/DD/YYYY', (value) => {
        if (!value) return true;
        if (!isValidDate(value)) return false;
        const parts = value.split('/');
        return checkValidDate(parts[0], parts[1], parts[2]);
      })
      .test('ins-dob-future', 'The Date of Birth can not be in the future.', (value) => {
        if (!value) return true;
        const parts = value.split('/');
        return checkFutureDate(parts[0], parts[1], parts[2]);
      }),
    insurance_gender: Yup.string().test('ins-gender-required', 'Insurance Holder Gender is required', function (value) {
      const p = this.parent as Record<string, unknown>;
      const through = String(p.insurance_through ?? '');
      if (carrierSelected(p.insurance_carrier_id) && through && through !== 'none' && through !== 'myself') {
        return !!value;
      }
      return true;
    }),
    insurance_address1: Yup.string().test(
      'ins-address1-required',
      'Insurance Holder Address is required',
      function (value) {
        const p = this.parent as Record<string, unknown>;
        const through = String(p.insurance_through ?? '');
        if (carrierSelected(p.insurance_carrier_id) && through && through !== 'none' && through !== 'myself') {
          return !!value;
        }
        return true;
      },
    ),
    insurance_city1: Yup.string().test('ins-city1-required', 'Insurance Holder City is required', function (value) {
      const p = this.parent as Record<string, unknown>;
      const through = String(p.insurance_through ?? '');
      if (carrierSelected(p.insurance_carrier_id) && through && through !== 'none' && through !== 'myself') {
        return !!value;
      }
      return true;
    }),
    insurance_state1: Yup.string().test('ins-state1-required', 'Insurance Holder State is required', function (value) {
      const p = this.parent as Record<string, unknown>;
      const through = String(p.insurance_through ?? '');
      if (carrierSelected(p.insurance_carrier_id) && through && through !== 'none' && through !== 'myself') {
        return !!value;
      }
      return true;
    }),
    insurance_zip_code1: Yup.string()
      .test('ins-zip1-required', 'Insurance Holder Zip Code is required', function (value) {
        const p = this.parent as Record<string, unknown>;
        const through = String(p.insurance_through ?? '');
        if (carrierSelected(p.insurance_carrier_id) && through && through !== 'none' && through !== 'myself') {
          return !!value;
        }
        return true;
      })
      .test('ins-zip1-valid', 'Not valid zip code', (value) => {
        if (value && !isValidZipCode(value)) return false;
        return true;
      }),
    state_funded_coverage_status: Yup.string().test(
      'state-funded-required',
      'Do you currently have insurance coverage through a state-funded program such as Medicaid?',
      function (value) {
        const p = this.parent as Record<string, unknown>;
        if (
          carrierSelected(p.insurance_carrier_id) ||
          (!!p.insurance_through && String(p.insurance_through).trim() !== '' && String(p.insurance_through) !== 'none')
        ) {
          return !!value;
        }
        return true;
      },
    ),
    iFirstName: Yup.string(),
    iMiddleName: Yup.string(),
    iLastName: Yup.string(),
    iDateOfBirth: Yup.string()
      .test('test-name', 'Enter Valid Date  MM/DD/YYYY', (value: any) => {
        if (value && !isValidDate(value)) {
          return false;
        }
        if (value) {
          const values = value?.split('/');
          if (!checkValidDate(values[0], values[1], values[2])) {
            return false;
          }
        }
        return true;
      })
      .test('test-future', 'The Date of Birth can not be in the future.', (value: any) => {
        if (value) {
          const values = value?.split('/');
          if (!checkFutureDate(values[0], values[1], values[2])) {
            return false;
          }
        }
        return true;
      }),
    iAddress1: Yup.string(),
    iCity: Yup.string(),
    iState: Yup.string(),
    iZipCode: Yup.string().test('test-name', 'Not valid zipcode', (value: any) => {
      if (value && !isValidZipCode(value)) {
        return false;
      }
      return true;
    }),
    // Secondary insurance — fields ordered to match the visual layout in InsuranceFormSection.
    // All conditions use .test() (not .when()) so Yup's topological sort does not reorder these
    // fields — validation runs in schema declaration order = visual order.
    secondary_insurance_carrier_id: Yup.number()
      .test('sec-carrier-required', 'Secondary Insurance carrier ID is required', function (value) {
        const p = this.parent as Record<string, unknown>;
        if (
          addSecondaryInsurance &&
          !!p.secondary_insurance_through &&
          String(p.secondary_insurance_through) !== 'none'
        ) {
          return value !== undefined && value !== null && Number(value) !== 0;
        }
        return true;
      })
      .nullable(),
    secondary_member_id: Yup.string().test(
      'sec-member-id-required',
      'Secondary Member ID is required',
      function (value) {
        const p = this.parent as Record<string, unknown>;
        if (
          addSecondaryInsurance &&
          (carrierSelected(p.secondary_insurance_carrier_id) ||
            (!!p.secondary_insurance_through && String(p.secondary_insurance_through) !== 'none'))
        ) {
          return !!value;
        }
        return true;
      },
    ),
    secondary_group_number: Yup.string().nullable(),
    // InsuranceFormSection uses secondary_insurance_*; SecondaryInsuranceFields uses secondary_i* — accept either.
    secondary_insurance_phone_number: Yup.string()
      .test('sec-phone-required', 'Secondary Insurance phone number is required', function (value) {
        const p = this.parent as Record<string, unknown>;
        if (
          addSecondaryInsurance &&
          (carrierSelected(p.secondary_insurance_carrier_id) ||
            (!!p.secondary_insurance_through && String(p.secondary_insurance_through) !== 'none'))
        ) {
          const modern = String(value ?? '').trim();
          const legacy = String(p.secondary_iPhoneNumber ?? '').trim();
          return !!(modern || legacy);
        }
        return true;
      })
      .test('sec-phone-valid', 'Enter Valid Phone Number', function (value) {
        const p = this.parent as Record<string, unknown>;
        const chosen = String(value ?? '').trim() || String(p.secondary_iPhoneNumber ?? '').trim();
        if (!chosen) return true;
        return isValidPhoneNumber(chosen);
      }),
    secondary_iPhoneNumber: Yup.string(),
    secondary_insurance_address: Yup.string()
      .test('sec-ins-address-required', 'Secondary Insurance Address is required', function (value) {
        const p = this.parent as Record<string, unknown>;
        if (
          addSecondaryInsurance &&
          (carrierSelected(p.secondary_insurance_carrier_id) ||
            (!!p.secondary_insurance_through && String(p.secondary_insurance_through) !== 'none'))
        ) {
          return !!value;
        }
        return true;
      })
      .test('sec-ins-address-no-empty-space', 'Secondary Insurance Address cannot be empty spaces', (value) => {
        if (value) return !/^\s+$/.test(value);
        return true;
      }),
    secondary_insurance_city: Yup.string().test(
      'sec-ins-city-required',
      'Secondary Insurance City is required',
      function (value) {
        const p = this.parent as Record<string, unknown>;
        if (
          addSecondaryInsurance &&
          (carrierSelected(p.secondary_insurance_carrier_id) ||
            (!!p.secondary_insurance_through && String(p.secondary_insurance_through) !== 'none'))
        ) {
          return !!value;
        }
        return true;
      },
    ),
    secondary_insurance_state: Yup.string().test(
      'sec-ins-state-required',
      'Secondary Insurance State is required',
      function (value) {
        const p = this.parent as Record<string, unknown>;
        if (
          addSecondaryInsurance &&
          (carrierSelected(p.secondary_insurance_carrier_id) ||
            (!!p.secondary_insurance_through && String(p.secondary_insurance_through) !== 'none'))
        ) {
          return !!value;
        }
        return true;
      },
    ),
    secondary_insurance_zip_code: Yup.string()
      .test('sec-ins-zip-required', 'Secondary Insurance ZipCode is required', function (value) {
        const p = this.parent as Record<string, unknown>;
        if (
          addSecondaryInsurance &&
          (carrierSelected(p.secondary_insurance_carrier_id) ||
            (!!p.secondary_insurance_through && String(p.secondary_insurance_through) !== 'none'))
        ) {
          return !!value;
        }
        return true;
      })
      .test('sec-ins-zip-valid', 'Not valid zipcode', (value) => {
        if (value && !isValidZipCode(value)) return false;
        return true;
      }),
    secondary_insurance_through: Yup.string().test(
      'sec-ins-through-required',
      'Please select who the secondary policy holder is',
      function (value) {
        const p = this.parent as Record<string, unknown>;
        if (addSecondaryInsurance && carrierSelected(p.secondary_insurance_carrier_id)) {
          return !!value && value !== 'none';
        }
        return true;
      },
    ),
    secondary_insurance_first_name: Yup.string()
      .test('sec-fn-required', 'Secondary Insurance Holder First Name is required', function (value) {
        const p = this.parent as Record<string, unknown>;
        const through = String(p.secondary_insurance_through ?? '');
        if (
          addSecondaryInsurance &&
          carrierSelected(p.secondary_insurance_carrier_id) &&
          through &&
          through !== 'none' &&
          through !== 'myself'
        ) {
          const modern = String(value ?? '').trim();
          const legacy = String(p.secondary_iFirstName ?? '').trim();
          return !!(modern || legacy);
        }
        return true;
      })
      .test('sec-fn-valid', 'Enter Valid First Name', function (value) {
        const p = this.parent as Record<string, unknown>;
        const chosen = String(value ?? '').trim() || String(p.secondary_iFirstName ?? '').trim();
        if (!chosen) return true;
        return isValidName(chosen);
      }),
    secondary_iFirstName: Yup.string(),
    secondary_iMiddleName: Yup.string(),
    secondary_insurance_last_name: Yup.string()
      .test('sec-ln-required', 'Secondary Insurance Holder Last Name is required', function (value) {
        const p = this.parent as Record<string, unknown>;
        const through = String(p.secondary_insurance_through ?? '');
        if (
          addSecondaryInsurance &&
          carrierSelected(p.secondary_insurance_carrier_id) &&
          through &&
          through !== 'none' &&
          through !== 'myself'
        ) {
          const modern = String(value ?? '').trim();
          const legacy = String(p.secondary_iLastName ?? '').trim();
          return !!(modern || legacy);
        }
        return true;
      })
      .test('sec-ln-valid', 'Enter Valid Last Name', function (value) {
        const p = this.parent as Record<string, unknown>;
        const chosen = String(value ?? '').trim() || String(p.secondary_iLastName ?? '').trim();
        if (!chosen) return true;
        return isValidName(chosen);
      }),
    secondary_iLastName: Yup.string(),
    secondary_insurance_date_of_birth: Yup.string()
      .test('sec-dob-required', 'Secondary Insurance Holder Date of birth is required', function (value) {
        const p = this.parent as Record<string, unknown>;
        const through = String(p.secondary_insurance_through ?? '');
        if (
          addSecondaryInsurance &&
          carrierSelected(p.secondary_insurance_carrier_id) &&
          through &&
          through !== 'none' &&
          through !== 'myself'
        ) {
          const modern = String(value ?? '').trim();
          const legacy = String(p.secondary_iDateOfBirth ?? '').trim();
          return !!(modern || legacy);
        }
        return true;
      })
      .test('sec-dob-valid', 'Enter Valid Date  MM/DD/YYYY', function (value) {
        const p = this.parent as Record<string, unknown>;
        const chosen = String(value ?? '').trim() || String(p.secondary_iDateOfBirth ?? '').trim();
        if (!chosen) return true;
        if (!isValidDate(chosen)) return false;
        const datevalues = chosen.split('/');
        return checkValidDate(datevalues[0], datevalues[1], datevalues[2]);
      })
      .test('sec-dob-future', 'The Secondary Date of Birth can not be in the future.', function (value) {
        const p = this.parent as Record<string, unknown>;
        const chosen = String(value ?? '').trim() || String(p.secondary_iDateOfBirth ?? '').trim();
        if (!chosen) return true;
        const datevalues = chosen.split('/');
        return checkFutureDate(datevalues[0], datevalues[1], datevalues[2]);
      }),
    secondary_iDateOfBirth: Yup.string(),
    secondary_insurance_address1: Yup.string()
      .test('sec-hold-addr-required', 'Secondary Insurance Holder Address is required', function (value) {
        const p = this.parent as Record<string, unknown>;
        const through = String(p.secondary_insurance_through ?? '');
        if (
          addSecondaryInsurance &&
          carrierSelected(p.secondary_insurance_carrier_id) &&
          through &&
          through !== 'none' &&
          through !== 'myself'
        ) {
          const modern = String(value ?? '').trim();
          const legacy = String(p.secondary_iAddress1 ?? '').trim();
          return !!(modern || legacy);
        }
        return true;
      })
      .test('sec-hold-addr-space', 'Secondary Insurance Holder Address cannot be empty spaces', function (value) {
        const p = this.parent as Record<string, unknown>;
        const chosen = String(value ?? '').trim() || String(p.secondary_iAddress1 ?? '').trim();
        if (!chosen) return true;
        return !/^\s+$/.test(chosen);
      }),
    secondary_iAddress1: Yup.string(),
    secondary_insurance_city1: Yup.string().test(
      'sec-hold-city-required',
      'Secondary Insurance Holder city Required',
      function (value) {
        const p = this.parent as Record<string, unknown>;
        const through = String(p.secondary_insurance_through ?? '');
        if (
          addSecondaryInsurance &&
          carrierSelected(p.secondary_insurance_carrier_id) &&
          through &&
          through !== 'none' &&
          through !== 'myself'
        ) {
          const modern = String(value ?? '').trim();
          const legacy = String(p.secondary_iCity ?? '').trim();
          return !!(modern || legacy);
        }
        return true;
      },
    ),
    secondary_iCity: Yup.string(),
    secondary_insurance_state1: Yup.string().test(
      'sec-hold-state-required',
      'Secondary Insurance Holder state Required',
      function (value) {
        const p = this.parent as Record<string, unknown>;
        const through = String(p.secondary_insurance_through ?? '');
        if (
          addSecondaryInsurance &&
          carrierSelected(p.secondary_insurance_carrier_id) &&
          through &&
          through !== 'none' &&
          through !== 'myself'
        ) {
          const modern = String(value ?? '').trim();
          const legacy = String(p.secondary_iState ?? '').trim();
          return !!(modern || legacy);
        }
        return true;
      },
    ),
    secondary_iState: Yup.string(),
    secondary_insurance_zip_code1: Yup.string()
      .test('sec-hold-zip-required', 'Secondary Insurance Holder ZipCode is required', function (value) {
        const p = this.parent as Record<string, unknown>;
        const through = String(p.secondary_insurance_through ?? '');
        if (
          addSecondaryInsurance &&
          carrierSelected(p.secondary_insurance_carrier_id) &&
          through &&
          through !== 'none' &&
          through !== 'myself'
        ) {
          const modern = String(value ?? '').trim();
          const legacy = String(p.secondary_iZipCode ?? '').trim();
          return !!(modern || legacy);
        }
        return true;
      })
      .test('sec-hold-zip-valid', 'Not valid zipcode', function (value) {
        const p = this.parent as Record<string, unknown>;
        const chosen = String(value ?? '').trim() || String(p.secondary_iZipCode ?? '').trim();
        if (!chosen) return true;
        return isValidZipCode(chosen);
      }),
    secondary_iZipCode: Yup.string().test('test-name', 'Not valid zipcode', (value: any) => {
      if (value && !isValidZipCode(value)) {
        return false;
      }
      return true;
    }),
    clientTag:
      clientTagsRequired && !isCollectorAddUser
        ? Yup.array()
            .transform((value, originalValue) => {
              if (Array.isArray(originalValue)) return originalValue;
              if (!originalValue || originalValue === '') return [];
              if (typeof originalValue === 'object') return [originalValue];
              return originalValue;
            })
            .min(1, 'Select at least one client tag')
            .required('Select at least one client tag')
        : Yup.array().nullable(),
    ...(isCollectorAddUser
      ? {
          client_tag_id: clientTagsRequired
            ? Yup.string().required('Select at least one client tag')
            : Yup.string().nullable(),
          client_division_id: divisionRequired ? Yup.string().required('Select a division') : Yup.string().nullable(),
        }
      : {
          organizations: Yup.array().nullable(),
        }),
  });
};
